/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

/**
 * Settings inside KnetDB's frame (openspec extend-model-providers, specs/settings-page): the home
 * page `p/settings` mounts `@knet/platform-ui`'s Settings bundle into the main area, so the top bar
 * and the left rail stay. KnetDB serves the bundle at `/settings/assets/` and the member's config
 * at `/settings/config`.
 */

import { makeT } from "app/client/lib/localization";
import { HomeModel } from "app/client/models/HomeModel";
import { testId, theme, vars } from "app/client/ui2018/cssVars";

import { dom, DomContents, Observable, styled } from "grainjs";

const t = makeT("KnetSettingsPage");

interface KnetSettingsApi {
  mount(element: HTMLElement, config: unknown, options?: { layout?: "page" | "fill" }): () => void;
}

interface SettingsConfigAnswer {
  config: unknown;
  assetVersion: string;
}

let lastWorkspaceId: number | undefined;

/** The workspace Settings opens on when the address names none: the last one opened in this tab. */
export function rememberSettingsWorkspace(id: number | undefined) {
  if (id) { lastWorkspaceId = id; }
}

let bundle: Promise<KnetSettingsApi> | null = null;

/** Load the bundle's CSS and script once per page; later visits reuse them. */
function loadBundle(version: string): Promise<KnetSettingsApi> {
  if (!bundle) {
    bundle = new Promise<KnetSettingsApi>((resolve, reject) => {
      const v = encodeURIComponent(version);
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = `/settings/assets/settings-app.css?v=${v}`;
      document.head.appendChild(css);
      const script = document.createElement("script");
      script.type = "module";
      script.src = `/settings/assets/settings-app.js?v=${v}`;
      script.onload = () => {
        const api = (window as unknown as { KnetSettings?: KnetSettingsApi }).KnetSettings;
        if (api) { resolve(api); }
        else { reject(new Error(t("Settings did not start."))); }
      };
      script.onerror = () => { reject(new Error(t("Settings could not be loaded."))); };
      document.head.appendChild(script);
    });
    bundle.catch(() => { bundle = null; });
  }
  return bundle;
}

/** `/settings/config` query: `?workspace=` from the address wins, then the last workspace, then the org. */
export function settingsConfigQuery(search: string, lastWs: number | undefined, orgDomain: string | undefined): string {
  const asked = Number(new URLSearchParams(search).get("workspace"));
  const query = new URLSearchParams();
  const ws = Number.isInteger(asked) && asked > 0 ? asked : lastWs;
  if (ws) { query.set("workspace", String(ws)); }
  if (orgDomain) { query.set("org", orgDomain); }
  return query.toString();
}

export function buildKnetSettingsPage(home: HomeModel): DomContents {
  if (!home.app.currentValidUser) {
    return cssPage(cssMessage(t("Sign in to change settings."), testId("knet-settings-message")));
  }
  const message = Observable.create<string | null>(null, t("Loading settings…"));
  const host = cssHost(testId("knet-settings"));
  let unmount: (() => void) | null = null;
  let disposed = false;

  (async () => {
    const query = settingsConfigQuery(window.location.search, lastWorkspaceId, home.app.currentOrg?.domain ?? undefined);
    const resp = await fetch(`/settings/config?${query}`, { credentials: "include", headers: { Accept: "application/json" } });
    const body = await resp.json().catch(() => ({})) as Partial<SettingsConfigAnswer> & { error?: string };
    if (!resp.ok || !body.config) { throw new Error(body.error || t("Settings unavailable")); }
    const api = await loadBundle(body.assetVersion ?? "0");
    if (disposed) { return; }
    unmount = api.mount(host, body.config, { layout: "fill" });
    message.set(null);
  })().catch((e: Error) => { if (!disposed) { message.set(e.message); } });

  return cssPage(
    dom.onDispose(() => {
      disposed = true;
      unmount?.();
      message.dispose();
    }),
    dom.maybe(message, m => cssMessage(m, testId("knet-settings-message"))),
    host,
  );
}

const cssPage = styled("div", `
  position: relative;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`);

const cssHost = styled("div", `
  flex: 1 1 0px;
  min-height: 0;
`);

const cssMessage = styled("div", `
  padding: 40px;
  color: ${theme.lightText};
  font-size: ${vars.mediumFontSize};
`);
